import {
  ShieldCheck,
  GraduationCap,
  BookOpen,
  Users,
  Briefcase,
  UserCheck,
} from "lucide-react";

const guides = [
  {
    slug: "admin",
    section: "guides",
    title: "Admin Guide",
    subtitle: "Complete reference for institute administrators managing Acadence",
    icon: ShieldCheck,
    readTime: "10 min",
    content: [
      {
        type: "callout",
        variant: "info",
        title: "Who is this for?",
        body: "This guide is for institute administrators — principals, registrars, or IT staff who have been granted the admin role. Admins have full access to every module and are responsible for configuring the system, managing users, and overseeing day-to-day operations across departments.",
      },
      {
        type: "role-badge",
        role: "admin",
      },
      {
        type: "heading",
        level: 2,
        text: "Your Dashboard",
      },
      {
        type: "text",
        text: "The admin dashboard gives you a real-time, institute-wide overview. At a glance you can see total enrolled students, active faculty members, pending fee collections, today's attendance rate across all departments, open HR leave requests, and any unread visitor check-ins. Quick-action cards let you jump directly into common tasks like adding a user, running payroll, or generating a report without navigating through the full menu.",
      },
      {
        type: "heading",
        level: 2,
        text: "First-Time Setup",
      },
      {
        type: "steps",
        items: [
          {
            title: "Configure your institute profile",
            description:
              "Go to Settings → Institute Profile and fill in the institute name, logo, address, contact email, and academic year. This information appears on all generated certificates, fee receipts, and official documents.",
          },
          {
            title: "Create departments and programs",
            description:
              "Navigate to Academics → Departments and add every department (e.g., Computer Science, Mechanical Engineering). Under each department, create the programs (B.Tech, M.Tech, MBA) and define semesters or years as applicable.",
          },
          {
            title: "Add users and assign roles",
            description:
              "Go to HR → Users and bulk-import or individually create accounts for faculty, HR staff, and placement officers. Students are typically imported via Admissions. Assign each person the correct role (admin, faculty, hr_manager, placement_officer, student, parent) — roles control exactly which modules they can see.",
          },
          {
            title: "Set up fee structures",
            description:
              "Under Fees → Fee Structures, define the tuition, hostel, lab, and miscellaneous fee heads for each program and academic year. You can configure installment schedules, late-fine rules, and scholarship/waiver categories here.",
          },
          {
            title: "Configure notification templates",
            description:
              "Go to Communications → Templates and review or edit the default WhatsApp and email templates for fee reminders, attendance alerts, exam notifications, and result announcements. Activate the WhatsApp integration if your institution uses it.",
          },
          {
            title: "Run a test cycle",
            description:
              "Create one test student, enroll them in a course, mark attendance, add a fee payment, and check the student-facing dashboard to confirm everything is working end-to-end before rolling out to the full institute.",
          },
        ],
      },
      {
        type: "heading",
        level: 2,
        text: "Modules Available to You",
      },
      {
        type: "list",
        items: [
          "Admissions — manage applications, document verification, merit lists, and enrollment",
          "Student Profiles — view and edit all student records, guardians, and academic history",
          "Attendance — institute-wide attendance reports and override capabilities",
          "LMS — course content management, assignment oversight, and progress tracking",
          "Exams — exam scheduling, hall ticket generation, seating arrangements",
          "Results — publish results, manage grade boundaries, and generate transcripts",
          "Fees — fee collection, receipts, outstanding reports, and waivers",
          "Certificates — approve and generate bonafide, migration, and conduct certificates",
          "Library — book inventory, issue/return logs, and fine management",
          "HR — employee profiles, leave approvals, and attendance tracking",
          "Payroll — salary processing, payslip generation, and compliance",
          "Documents — upload and manage institutional documents and policies",
          "Placement — company pipeline, drive scheduling, and placement statistics",
          "Mentor-Mentee — assign mentors to student groups and track meeting logs",
          "OBE/NAAC — outcome-based education mapping and accreditation data",
          "Feedback — manage student and faculty feedback forms and view analytics",
          "Visitor Management — log and track all campus visitors",
          "Inward/Outward — track official correspondence and couriers",
          "Communications — broadcast announcements via email, SMS, and WhatsApp",
          "Reports — generate and export any report across all modules",
          "Settings — institute profile, roles, integrations, and system configuration",
        ],
      },
      {
        type: "callout",
        variant: "tip",
        title: "Start-of-Semester Checklist",
        body: "Before each new semester: (1) Roll over the academic year in Settings. (2) Import new student batch via Admissions. (3) Assign courses and faculty in LMS. (4) Activate new fee structures. (5) Generate hall tickets for any incoming exams. (6) Send a welcome communication to all new students and parents.",
      },
      {
        type: "callout",
        variant: "tip",
        title: "End-of-Semester Checklist",
        body: "At semester close: (1) Ensure all attendance is finalized and submitted by faculty. (2) Verify exam results are published. (3) Run the outstanding fees report and send reminders. (4) Approve pending certificate requests. (5) Archive the semester data in Reports. (6) Update the placement statistics before the next batch arrives.",
      },
    ],
  },

  {
    slug: "student",
    section: "guides",
    title: "Student Guide",
    subtitle: "Everything you need to navigate your academic journey in Acadence",
    icon: GraduationCap,
    readTime: "8 min",
    content: [
      {
        type: "callout",
        variant: "info",
        title: "Who is this for?",
        body: "This guide is for students enrolled at institutions using Acadence. From the moment you log in, you have access to your courses, attendance records, exam schedules, fee statements, and results — all in one place.",
      },
      {
        type: "role-badge",
        role: "student",
      },
      {
        type: "heading",
        level: 2,
        text: "Your Dashboard",
      },
      {
        type: "text",
        text: "Your student dashboard is your academic home screen. It shows your current semester's enrolled courses with progress bars, your overall attendance percentage (with a colour-coded warning if you fall below the minimum threshold), upcoming exams and submission deadlines, your outstanding fee balance, and any recent notifications from faculty or administration. Everything is personalised to your program, year, and section.",
      },
      {
        type: "heading",
        level: 2,
        text: "Getting Started",
      },
      {
        type: "steps",
        items: [
          {
            title: "Log in and complete your profile",
            description:
              "Use the credentials provided by your institution to log in. Navigate to your Profile page and upload a photo, verify your mobile number, and confirm your guardian details. A complete profile is required before certificate requests can be processed.",
          },
          {
            title: "Explore your enrolled courses",
            description:
              "Open the LMS module to see all courses you are enrolled in for this semester. Each course has a syllabus, lecture notes, video links, and assignments uploaded by your faculty. Download course materials early so you always have offline copies.",
          },
          {
            title: "Check your attendance regularly",
            description:
              "The Attendance module shows your subject-wise attendance percentage updated after every class. If any subject shows below 75 % (or your institution's minimum), take action immediately by speaking to the subject faculty or your mentor.",
          },
          {
            title: "Stay on top of fees",
            description:
              "Go to Fees to see your current fee dues, payment history, and upcoming installment dates. You can pay online via the portal or download a challan for offline payment. Always save your payment receipt for future reference.",
          },
          {
            title: "Monitor exam schedules and results",
            description:
              "Before exams, download your hall ticket from the Exams module and check the seating arrangement. After results are published, view your mark sheet in the Results module and download your grade card as a PDF.",
          },
        ],
      },
      {
        type: "heading",
        level: 2,
        text: "Modules Available to You",
      },
      {
        type: "list",
        items: [
          "LMS — access course materials, lectures, and assignments for all enrolled subjects",
          "Attendance — view subject-wise and overall attendance with date-level detail",
          "Fees — check fee dues, pay online, and download receipts",
          "Exams — view exam schedule, download hall ticket, and check seating",
          "Results — view mark sheets, grade cards, and semester GPA",
          "Certificates — request bonafide, migration, or conduct certificates",
          "Library — search the catalogue, check borrowed books, and view due dates",
          "Feedback — submit anonymous feedback on courses and faculty",
          "Placement — view active job drives, register interest, and track application status",
        ],
      },
      {
        type: "callout",
        variant: "tip",
        title: "Paying Fees Online",
        body: "Go to Fees → Pay Now, select the installment or fee head you want to clear, and choose your payment method. After a successful transaction, an instant receipt is emailed to your registered address and also saved under Fees → Payment History. If the payment gateway times out, always check Payment History before retrying to avoid a double charge.",
      },
      {
        type: "callout",
        variant: "tip",
        title: "Checking Your Results",
        body: "Results are published by the admin or faculty after internal verification. You will receive a notification when they go live. Navigate to Results → Current Semester to view subject-wise marks and your SGPA. For previous semesters, use Results → History. You can download a PDF grade card for any semester — keep these safe as they may be required for scholarships, internship applications, and further studies.",
      },
    ],
  },

  {
    slug: "faculty",
    section: "guides",
    title: "Faculty Guide",
    subtitle: "Manage courses, attendance, evaluations, and student progress",
    icon: BookOpen,
    readTime: "8 min",
    content: [
      {
        type: "callout",
        variant: "info",
        title: "Who is this for?",
        body: "This guide is for teaching faculty members — lecturers, assistant professors, professors, and lab instructors — who use Acadence to manage their courses, mark attendance, upload content, conduct assessments, and track student progress.",
      },
      {
        type: "role-badge",
        role: "faculty",
      },
      {
        type: "heading",
        level: 2,
        text: "Your Dashboard",
      },
      {
        type: "text",
        text: "Your faculty dashboard highlights everything that needs your attention today. You can see your teaching schedule for the day (room, time, subject, and batch), the number of pending assignment submissions waiting for evaluation, any students in your mentor group flagged for low attendance or poor performance, and recent announcements from administration. The dashboard is designed to give you a zero-effort daily briefing so you can start each day prepared.",
      },
      {
        type: "heading",
        level: 2,
        text: "Daily Workflow",
      },
      {
        type: "steps",
        items: [
          {
            title: "Review your daily schedule",
            description:
              "Check the timetable widget on your dashboard or open Attendance → Timetable to see today's classes. Note any exam invigilation duties or lab sessions that differ from the regular schedule.",
          },
          {
            title: "Mark attendance after each class",
            description:
              "Open Attendance → Mark Attendance, select the subject and batch, and mark each student present, absent, or on leave. Submit before leaving the classroom — late submissions are flagged by the system. Students are automatically notified of their attendance status.",
          },
          {
            title: "Upload course content in LMS",
            description:
              "Navigate to LMS → My Courses, select the relevant course, and upload lecture notes, slides, reference PDFs, or video links after each session. Use the Announcement feature within a course to post important updates directly to enrolled students.",
          },
          {
            title: "Create and evaluate assignments",
            description:
              "Under LMS → Assignments, create new assignments with deadlines, maximum marks, and submission instructions. Once the deadline passes, review submissions one by one, enter marks and feedback, and publish the grades. Students receive a notification when their grade is released.",
          },
          {
            title: "Enter internal exam marks",
            description:
              "After internal assessments are conducted, go to Exams → Enter Marks, select the exam and subject, and enter each student's score. Double-check entries before finalising — corrections require admin approval after submission.",
          },
          {
            title: "Log mentor meetings",
            description:
              "If you are assigned as a mentor, open Mentor-Mentee → My Mentees to see your group. After each mentoring session, log the date, topics discussed, and any action items. Flag students who need counselling or administrative intervention.",
          },
        ],
      },
      {
        type: "heading",
        level: 2,
        text: "Modules Available to You",
      },
      {
        type: "list",
        items: [
          "Attendance — mark, edit (within grace period), and report on student attendance",
          "LMS — manage course content, assignments, announcements, and student submissions",
          "Exams — view exam schedules, enter internal marks, and manage question banks",
          "Results — view and verify published results for your subjects",
          "Mentor-Mentee — manage your assigned mentee group and log session notes",
          "OBE/NAAC — map course outcomes to program outcomes and enter attainment data",
        ],
      },
      {
        type: "callout",
        variant: "tip",
        title: "Identifying Struggling Students Early",
        body: "Use LMS → Course Progress to see each student's engagement score — it combines attendance, assignment submission rate, and time spent on materials. Students below 50% engagement are at risk of falling behind. Reach out to them directly via the messaging feature or escalate to your department coordinator early in the semester rather than waiting for exam results to reveal the problem.",
      },
    ],
  },

  {
    slug: "parent",
    section: "guides",
    title: "Parent Guide",
    subtitle: "Stay connected with your child's academic progress and fees",
    icon: Users,
    readTime: "5 min",
    content: [
      {
        type: "callout",
        variant: "info",
        title: "Who is this for?",
        body: "This guide is for parents and guardians of enrolled students. The parent portal gives you read-only visibility into your child's attendance, fee status, exam results, and important announcements — and lets you pay fees directly without needing to visit the institution.",
      },
      {
        type: "role-badge",
        role: "parent",
      },
      {
        type: "heading",
        level: 2,
        text: "Your Dashboard",
      },
      {
        type: "text",
        text: "The parent dashboard is a focused summary of the information that matters most to you. You will see your child's current attendance percentage with a clear indicator if it is below the required minimum, any upcoming fee due dates and the outstanding amount, the most recent exam results published by the institution, and any important notifications from the administration such as holidays, exam schedules, or event announcements. If you have more than one child enrolled at the same institution, you can switch between their profiles from the top of the dashboard.",
      },
      {
        type: "heading",
        level: 2,
        text: "What You Can Do",
      },
      {
        type: "list",
        items: [
          "View attendance — see your child's subject-wise daily attendance and overall percentage for the current semester",
          "Pay fees online — clear tuition, hostel, or other fee dues directly through the portal and download official receipts instantly",
          "Check exam results — view published mark sheets and download grade cards as PDFs",
          "Monitor fee status — see the full fee statement including paid installments, pending dues, fines, and any scholarship adjustments",
          "Receive notifications — get WhatsApp or email alerts for low attendance warnings, fee reminders, result publications, and institute announcements",
          "Download certificates — access bonafide and other certificates approved for your child",
        ],
      },
      {
        type: "heading",
        level: 2,
        text: "Getting Started",
      },
      {
        type: "steps",
        items: [
          {
            title: "Log in with your parent credentials",
            description:
              "The institution will provide your login email and a temporary password at the time of your child's enrollment. Use these to access the portal at the institution's Acadence URL. Change your password immediately after the first login from the Profile page.",
          },
          {
            title: "Explore the parent dashboard",
            description:
              "Familiarise yourself with the dashboard. The attendance widget turns red if attendance drops below the threshold — this is your early warning signal. The fees widget shows the next due date prominently so you are never caught off guard.",
          },
          {
            title: "Set up notification preferences",
            description:
              "Go to Profile → Notifications and enable the alerts that are most useful to you. We recommend enabling WhatsApp notifications for low attendance, fee due reminders, and result publications so that you receive real-time updates without needing to log in.",
          },
        ],
      },
      {
        type: "callout",
        variant: "tip",
        title: "Enable WhatsApp Notifications",
        body: "WhatsApp notifications are the fastest way to stay informed. Once enabled, you will receive an instant message whenever your child's attendance drops below the minimum, a fee installment is due in the next 3 days, or new results are published. To activate, go to Profile → Notifications → WhatsApp, enter your WhatsApp-registered mobile number, and confirm with the one-time verification code sent to your phone.",
      },
    ],
  },

  {
    slug: "hr-manager",
    section: "guides",
    title: "HR Manager Guide",
    subtitle: "Manage employee records, payroll, leaves, and compliance",
    icon: Briefcase,
    readTime: "7 min",
    content: [
      {
        type: "callout",
        variant: "info",
        title: "Who is this for?",
        body: "This guide is for HR Managers responsible for managing the institution's workforce — tracking employee attendance, processing leave requests, running monthly payroll, generating payslips, and maintaining compliance documents. The hr_manager role gives you full access to HR and Payroll modules.",
      },
      {
        type: "role-badge",
        role: "hr_manager",
      },
      {
        type: "heading",
        level: 2,
        text: "Your Dashboard",
      },
      {
        type: "text",
        text: "The HR Manager dashboard gives you a workforce snapshot at the start of each day. You can see total active employees broken down by department, the number of pending leave requests awaiting your approval, today's employee attendance status (present, absent, on leave), upcoming payroll processing dates, and any compliance documents with approaching renewal deadlines. Action items are surfaced prominently so nothing slips through the cracks.",
      },
      {
        type: "heading",
        level: 2,
        text: "Monthly Workflow",
      },
      {
        type: "steps",
        items: [
          {
            title: "Review and finalise employee attendance",
            description:
              "At the start of each month, navigate to HR → Attendance Report and review the previous month's attendance for all employees. Correct any discrepancies with department heads before closing the attendance period. Accurate attendance is the foundation for payroll calculation.",
          },
          {
            title: "Process and approve leave requests",
            description:
              "Open HR → Leave Management to see all pending leave applications. Review the employee's remaining leave balance, check department staffing levels, and approve or reject with a note. Approved leaves automatically update the attendance records and are factored into salary computation.",
          },
          {
            title: "Run payroll processing",
            description:
              "Navigate to Payroll → Process Payroll, select the month and year, and initiate the payroll run. The system calculates gross salary, applies leave deductions, adds allowances, deducts TDS and PF contributions based on each employee's payroll configuration, and produces a payroll summary for your review. Verify the totals, make any manual adjustments, and approve the payroll.",
          },
          {
            title: "Generate and distribute payslips",
            description:
              "After payroll is approved, go to Payroll → Payslips and generate payslips for all employees in bulk. Payslips can be emailed directly to each employee's registered address or downloaded as a ZIP of PDFs for distribution through your own channel.",
          },
          {
            title: "Maintain compliance documents",
            description:
              "Go to Documents → HR Compliance and upload or renew mandatory documents such as PF registration, ESI certificates, and shop-and-establishment licences. The system tracks expiry dates and alerts you 30 days before a document is due for renewal.",
          },
          {
            title: "Track inward and outward correspondence",
            description:
              "Use Inward/Outward to log official HR correspondence — appointment letters, warning notices, resignation acceptances, and government communications. Maintain a searchable register for audit purposes.",
          },
        ],
      },
      {
        type: "heading",
        level: 2,
        text: "Modules Available to You",
      },
      {
        type: "list",
        items: [
          "HR — employee profiles, leave management, attendance tracking, and onboarding",
          "Payroll — salary processing, payslip generation, allowances, and deductions",
          "Documents — upload, manage, and track compliance and HR policy documents",
          "Visitor Management — log all campus visitors and generate visit reports",
          "Inward/Outward — maintain registers for official correspondence",
          "Communications — send bulk email or WhatsApp messages to staff",
          "Reports — generate HR and payroll reports for management and audit",
        ],
      },
      {
        type: "callout",
        variant: "tip",
        title: "Payroll Processing Tips",
        body: "Always freeze the attendance period before running payroll to prevent retroactive changes. Use the Payroll → Variance Report to compare the current month's payroll totals against the previous month — any unusually large variance (more than 10%) is worth investigating before approving. Keep a separate backup of approved payroll summaries in Documents for at least 7 years to meet statutory audit requirements.",
      },
    ],
  },

  {
    slug: "placement-officer",
    section: "guides",
    title: "Placement Officer Guide",
    subtitle: "Drive campus placements from company pipeline to final offers",
    icon: UserCheck,
    readTime: "7 min",
    content: [
      {
        type: "callout",
        variant: "info",
        title: "Who is this for?",
        body: "This guide is for Placement Officers and Training & Placement Cell (TPC) coordinators who manage campus recruitment. You will use Acadence to maintain the company pipeline, schedule placement drives, track student applications, manage interview rounds, record final offers, and publish placement statistics.",
      },
      {
        type: "role-badge",
        role: "placement_officer",
      },
      {
        type: "heading",
        level: 2,
        text: "Your Dashboard",
      },
      {
        type: "text",
        text: "The placement officer dashboard tracks the health of the entire placement season at a glance. You can see the overall placement rate as a percentage of eligible students, the number of companies in your active pipeline at each stage (approached, confirmed, scheduled, completed), total offer letters issued, the highest and average package offered so far, and students who are yet to receive any offer. This dashboard becomes your command centre during the placement season.",
      },
      {
        type: "heading",
        level: 2,
        text: "Placement Season Workflow",
      },
      {
        type: "steps",
        items: [
          {
            title: "Build and manage the company pipeline",
            description:
              "Open Placement → Companies and add every company you are in discussions with. Record the contact person, job roles offered, CTC range, eligibility criteria (CGPA, backlogs, branches), and the current stage in your outreach. Update the stage as conversations progress from 'Interested' through 'MOU Signed' to 'Drive Confirmed'.",
          },
          {
            title: "Schedule placement drives",
            description:
              "Once a company confirms participation, go to Placement → Drives → New Drive and fill in the drive date, venue, company, eligible batches/branches, and the rounds (aptitude, group discussion, technical interview, HR interview). Publish the drive so eligible students can see it and register their interest.",
          },
          {
            title: "Manage student applications and shortlists",
            description:
              "Under Placement → Drives → [Drive Name] → Applications, review students who have registered. Apply eligibility filters (CGPA, no active backlogs, correct branch) to generate the shortlist. Share the shortlist with the company and update it based on their feedback.",
          },
          {
            title: "Track interview rounds",
            description:
              "During the drive, update each student's round-by-round status in real time — Selected, Waitlisted, or Eliminated. This gives you and the institute management a live picture of how the drive is progressing and lets you quickly identify how many students are still in contention.",
          },
          {
            title: "Record final offers and offer letters",
            description:
              "After the company communicates its final selections, go to Placement → Offers, enter each student's offer details (company, role, location, CTC), and upload the offer letter PDF. Students receive an automatic notification. Multiple offers are tracked so you can enforce the institution's offer acceptance policy.",
          },
          {
            title: "Generate placement reports",
            description:
              "Use Reports → Placement Statistics to generate summary and detailed reports for management review, NAAC accreditation, and public disclosure. Reports can be filtered by batch, branch, company sector, and CTC range, and exported as Excel or PDF.",
          },
        ],
      },
      {
        type: "heading",
        level: 2,
        text: "Modules Available to You",
      },
      {
        type: "list",
        items: [
          "Placement — full access to company pipeline, drives, student applications, offers, and statistics",
          "Communications — send targeted emails and WhatsApp broadcasts to eligible students about upcoming drives",
          "Reports — generate and export placement statistics for internal and accreditation reporting",
        ],
      },
      {
        type: "callout",
        variant: "tip",
        title: "Start Your Pipeline Early",
        body: "The most common reason for a poor placement season is starting the company outreach too late. Begin building your pipeline 2 to 3 months before the placement season officially opens. Use Placement → Companies to track every conversation from the first cold email onwards. Companies that participated in previous years are the easiest wins — reach out to them first, confirm dates, and use those early confirmations to build social proof when approaching new companies.",
      },
    ],
  },
];

export default guides;
